import React, { useState, useMemo, useCallback } from "react";
import { StyledButton, StyledH2 } from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";
import classnames from "classnames";
import { useSnapshot } from "valtio";

import { capitalize } from "../../../../helpers/general";
import CustomColumnDisplay from "@components/Common/CustomColumnDisplay";
import { store } from "../../../../components/VerticalLayout/store";
import { useAuthHeader } from "../../../../api/headersKeycloak";
import {
    ITEM_TRANSLATION_MAP,
    OPERATION_LABELS,
} from "../../../../constants/pos";

interface HistoryModalProps {
    selectedRows: any;
    isModalOpened: boolean;
    setIsModalOpened: (value: boolean) => void;
    setIsDataUpdated: (value: boolean) => void;
    setIsAddApplication: (value: boolean) => void;
}
export default function HistoryModal({
    selectedRows,
    isModalOpened,
    setIsModalOpened,
    setIsDataUpdated,
    setIsAddApplication,
}: HistoryModalProps) {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const { franchiseID, userID } = useSnapshot(store);

    const [dataIsUpdated, setDataIsUpdated] = useState<boolean | number>(false);
    const [data, setData] = useState<any>({});
    const [filterActive, setLocalFilterActive] = useState(false);

    const i18nextLng = useMemo(
        () => localStorage.getItem("i18nextLng") || "",
        []
    );
    const hasMultipleApps = selectedRows.length > 1;

    const handleClose = useCallback(() => {
        setIsModalOpened(false);
        setIsAddApplication(false);
    }, [setIsModalOpened, setIsAddApplication]);

    const url = useMemo(() => {
        const app = selectedRows
            .filter((row: any) => row.shopId && row.appId)
            .map((row: any) => ({
                [row.shopId]: row.appId,
            }));
        const appParams = app.map((el: any) => JSON.stringify(el)).join();
        return `${process.env.REACT_APP_API_V2_URL}/settings/general/application_pos/history?userId=${userID}&franchiseId=${franchiseID}&application=${appParams}`;
    }, [userID, franchiseID, selectedRows]);

    const requestHeader = useMemo(
        () => ({
            "Accept-Language": i18nextLng,
            ...getAuthHeader,
        }),
        [i18nextLng, getAuthHeader]
    );

    const extractActionValues = useCallback(
        (actions: any[], key: "oldValue" | "newValue") => {
            return actions
                .filter(
                    (el: any) =>
                        el.item !== "shopId" &&
                        (key === "newValue" || el.item !== "isActive")
                )
                .map((el: any) => el[key]);
        },
        []
    );

    const translateActionItems = useCallback(
        (actions: any[]) => {
            return actions
                .filter(
                    (el: any) =>
                        el.item !== "isActive" && ITEM_TRANSLATION_MAP[el.item]
                )
                .map((el: any) => t(ITEM_TRANSLATION_MAP[el.item]));
        },
        [t]
    );

    const renderArchiveStatus = useCallback(
        (isArchived: boolean) => (
            <span>
                {t("Status")}: {t(isArchived ? "Archived" : "Unarchived")}
            </span>
        ),
        [t]
    );

    const arrayOfCustomColumns: customColumnProps[] = useMemo(
        () => [
            {
                indexOFColumn: 1,
                columnName: t("Actions"),
                customJsx: ({ selectedRow }: any) => {
                    const { operation, actions } = selectedRow;
                    if (operation === "modify") {
                        const actionsText = translateActionItems(actions);
                        return (
                            <span>
                                {t("Modification")}:{actionsText.join(",")}
                            </span>
                        );
                    }

                    if (OPERATION_LABELS[operation]) {
                        return <span>{t(OPERATION_LABELS[operation])}</span>;
                    }

                    if (
                        operation.includes("Dissociation") ||
                        operation.includes("Associate") ||
                        operation.includes("Association")
                    ) {
                        return <span>{operation}</span>;
                    }

                    return null;
                },
            },
            {
                indexOFColumn: 2,
                columnName: t("Before modification"),
                customJsx: ({ selectedRow }: any) => {
                    const { operation, actions } = selectedRow;

                    switch (operation) {
                        case "modify":
                            const oldValues = extractActionValues(
                                actions,
                                "oldValue"
                            );
                            return (
                                <CustomColumnDisplay
                                    text={oldValues.join(",")}
                                />
                            );
                        case "creation":
                            return null;
                        case "Désarchivage":
                            return renderArchiveStatus(true);
                        case "Archivage":
                            return renderArchiveStatus(false);
                        default:
                            return <span>{""}</span>;
                    }
                },
            },
            {
                indexOFColumn: 3,
                columnName: t("After modification"),
                customJsx: ({ selectedRow }: any) => {
                    const { operation, actions } = selectedRow;

                    switch (operation) {
                        case "modify":
                            const newValues = extractActionValues(
                                actions,
                                "newValue"
                            );
                            return (
                                <CustomColumnDisplay
                                    text={newValues.join(",")}
                                />
                            );
                        case "creation":
                            return selectedRow[t("Designation")] ?? "--";
                        case "Désarchivage":
                            return renderArchiveStatus(false);
                        case "Archivage":
                            return renderArchiveStatus(true);
                        default:
                            return <span>{""}</span>;
                    }
                },
            },
        ],
        [t, translateActionItems, extractActionValues, renderArchiveStatus]
    );

    const displayedDesignation = useMemo(() => {
        if (hasMultipleApps) return null;
        return data?.data?.[0]?.[t("Designation")] ?? "";
    }, [hasMultipleApps, data, t]);

    const ActionColumn = useCallback(
        ({ selectedRow }: any) =>
            selectedRow.subRows === undefined ? <div /> : null,
        []
    );

    return (
        <Modal
            centered
            backdrop="static"
            fade={false}
            scrollable
            className="modal-wrapper__clz"
            isOpen={isModalOpened}
        >
            <ModalHeader toggle={handleClose} className="dev_icn_HSclose">
                <StyledH2 className="dev_txt_HStitleHistory">
                    {capitalize(t("Historical"))}
                </StyledH2>
            </ModalHeader>

            <ModalBody>
                <div
                    className={classnames("title__clz", {
                        "not-allowed-icon__clz": hasMultipleApps,
                    })}
                >
                    {t("Designation")} :{displayedDesignation}
                </div>

                <DynamicTable
                    url={url}
                    canResize
                    actionColumn={ActionColumn}
                    showGlobalFilter
                    requestHeader={requestHeader}
                    showFilter
                    setLocalFilterActive={setLocalFilterActive}
                    filterActive={filterActive}
                    setDataIsUpdated={setDataIsUpdated}
                    dataIsUpdated={dataIsUpdated}
                    elevationTable={1}
                    minHeight="500px"
                    maxHeight="500px"
                    name="historyProject"
                    arrayOfCustomColumns={arrayOfCustomColumns}
                    setData={setData}
                />
            </ModalBody>

            <ModalFooter className="border-top-0">
                <StyledButton
                    variant="primary"
                    className="fed_btn_HSclose"
                    onClick={handleClose}
                    rounded
                >
                    {t("Close")}
                </StyledButton>
            </ModalFooter>
        </Modal>
    );
}
